/**
 * Photos are uploaded with their rotation baked into the PIXELS.
 *
 * A camera does not rotate its sensor: it writes a landscape buffer and records the rotation as an
 * EXIF tag. Several screens here upload the picked `File` byte for byte, so those bytes went to
 * the server sideways with a note attached — and while a browser honours that note when it
 * DISPLAYS the image, the access-control devices and anything reading the bytes server-side do
 * not. Users reported exactly this: photos uploaded "inverted" instead of upright.
 *
 * Both mobile apps fix it at their own encoders (UploadOrientationTests on iOS,
 * PhotoOrientationTest on Android). This is the same fix for the web, and it uses the same long-edge
 * cap so a photo looks the same whichever surface produced it.
 *
 * See uploadPhotoOrientation.test.ts.
 */

/** Long edge a photo is reduced to before upload. Same figure as both apps. */
export const UPLOAD_PHOTO_MAX_DIMENSION = 1600;

/** JPEG quality for the re-encode. Same figure the mobile apps use. */
const UPLOAD_PHOTO_QUALITY = 0.85;

/**
 * The pixel size a photo should end up at: long edge capped, aspect ratio preserved.
 *
 * Neither edge is ever 0 — an extreme panorama would otherwise round its short edge away, and a
 * zero-sized canvas throws rather than producing a small image.
 */
export function uploadPhotoTargetSize(
  width: number,
  height: number,
  maxDimension: number = UPLOAD_PHOTO_MAX_DIMENSION,
): { width: number; height: number } {
  const longest = Math.max(width, height);
  if (longest <= maxDimension || longest <= 0 || maxDimension <= 0) {
    return { width, height };
  }
  const scale = maxDimension / longest;
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  };
}

/**
 * The file an upload should send: upright pixels, long edge capped, encoded once.
 *
 * Anything that is not an image — a PDF attached to a norma, a spreadsheet — is returned
 * UNTOUCHED: re-encoding those would destroy them.
 *
 * Never throws and never returns nothing. A browser without `createImageBitmap`, a file that will
 * not decode, a canvas that refuses: the original file is sent instead. A rotated photo is a
 * nuisance; a failed upload is a porteiro standing at a gate.
 */
export async function normalizePhotoForUpload(
  file: File,
  maxDimension: number = UPLOAD_PHOTO_MAX_DIMENSION,
): Promise<File> {
  if (!file || !file.type || !file.type.startsWith('image/')) return file;
  // An SVG has no pixels to rotate and would be rasterised by this.
  if (file.type === 'image/svg+xml') return file;

  try {
    const decode = (globalThis as any).createImageBitmap as
      | ((blob: Blob, options?: Record<string, unknown>) => Promise<ImageBitmap>)
      | undefined;
    if (typeof decode !== 'function') return file;

    // `from-image` is what applies the EXIF rotation to the decoded pixels. It is the spec default
    // in current browsers, but it is stated here because the whole point of this function is that
    // rotation, and a default is not a guarantee.
    const bitmap = await decode(file, { imageOrientation: 'from-image' });
    const { width, height } = uploadPhotoTargetSize(bitmap.width, bitmap.height, maxDimension);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) return file;
    context.drawImage(bitmap as unknown as CanvasImageSource, 0, 0, width, height);
    if (typeof (bitmap as any).close === 'function') (bitmap as any).close();

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', UPLOAD_PHOTO_QUALITY);
    });
    if (!blob) return file;

    const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
    return new File([blob], name, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    // Any failure at all sends the original: an upload must not be blocked by this.
    return file;
  }
}
